// Lógica pura do modal de parcela (reforma de 2026-09-01) — sem antd, sem
// React, testável no jest (parcelaForm.test.ts).
//
// Título × Descrição: as colunas parcela_titulo e descricao são gêmeas na
// prática (todos os writers gravam o mesmo texto nas duas), mas NÃO dá pra
// parar de gravar `descricao`: o e-mail "NF a emitir" (contasReceber.js) e o
// de BM assinado (bm.js) leem `descricao` CRUA, sem fallback; o Conta Azul só
// escreve `descricao`; e parcela de ADITIVO diverge de propósito (descricao =
// motivo do aditivo). A tela mostra UM campo ("Título da parcela") e um
// segundo ("Descrição detalhada") SÓ quando os dois divergem de verdade.

export type ModoTituloDesc = 'unico' | 'divergente';

const t = (s: string | null | undefined) => String(s ?? '').trim();

/** Semeadura ao abrir/hidratar o modal: decide o modo e os valores iniciais.
 *  - 'unico': descricao vazia, igual ao título, ou título vazio (parcela do
 *    Conta Azul: parcela_titulo NULL — o campo único semeia com a descricao).
 *  - 'divergente': os dois preenchidos e diferentes (aditivo; parcela CA cuja
 *    descricao o sync re-sobrescreveu depois de a pessoa dar um título). */
export function semearTituloDescricao(p: {
  parcela_titulo?: string | null; descricao?: string | null;
}): { modo: ModoTituloDesc; titulo: string; descricao: string } {
  const titulo = t(p.parcela_titulo);
  const descricao = t(p.descricao);
  if (!titulo) return { modo: 'unico', titulo: descricao, descricao };
  if (!descricao || descricao === titulo) return { modo: 'unico', titulo, descricao };
  return { modo: 'divergente', titulo, descricao };
}

/** Payload do salvar. Em 'unico' grava o MESMO texto nas DUAS colunas (mantém
 *  vivos os e-mails que leem descricao crua). Em 'divergente' cada campo vai
 *  pra sua coluna — e esvaziar a descrição detalhada colapsa de volta pro
 *  modo único (as duas recebem o título; string vazia crua quebraria os
 *  e-mails). Nunca devolve null: coluna varchar aceita '' e o update parcial
 *  do backend só escreve o que vier aqui.
 *
 *  haviaTexto: se a parcela TINHA texto (título ou descrição) e o campo único
 *  foi esvaziado, devolve {} — as duas chaves ficam FORA do payload e o banco
 *  preserva o que tinha (revisão adversarial de 01/09: selecionar-tudo-e-
 *  apagar zerava também a descricao, que os e-mails automáticos leem crua;
 *  antes da reforma só o título zerava). Sem texto anterior, vazio é vazio. */
export function montarPayloadTituloDescricao(
  modo: ModoTituloDesc, titulo: string, descricao: string, haviaTexto = false,
): { parcela_titulo?: string; descricao?: string } {
  const tit = t(titulo);
  if (modo === 'divergente') {
    const desc = t(descricao);
    if (!tit && haviaTexto) return {};
    return desc ? { parcela_titulo: tit, descricao: desc }
                : { parcela_titulo: tit, descricao: tit };
  }
  if (!tit && haviaTexto) return {};
  return { parcela_titulo: tit, descricao: tit };
}

export type Destinatario = { tipo: 'contato' | 'email'; id?: string; email?: string };

// Caracteres de e-mail de verdade: a régua antiga (`[^@\s]+@...`) aceitava
// "(a@x.com)", "a@x.com." e "mailto:a@x.com", que o emissor da nota recusa
// (revisão adversarial de 21/09).
const EMAIL_RE = /^[a-z0-9._%+'-]+@[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/i;

/** Um pedaço de texto que tem "@", já limpo: sem "mailto:", sem aspas,
 *  parênteses e colchetes nas pontas e sem a pontuação grudada no fim da frase
 *  ("fin@x.com.br."). `bruto` é o que a pessoa escreveu, para devolver o que
 *  não entrou exatamente como estava. */
export type PedacoEmail = { bruto: string; email: string; valido: boolean };

/** A régua ÚNICA de "ler e-mails de um texto livre" — usada no campo de digitar
 *  e na sugestão do campo antigo email_faturamento (que é texto herdado do
 *  Notion: tem e-mails separados por vírgula, ponto e vírgula, espaço, quebra
 *  de linha e " e "). Pedaço sem "@" (nome, "e", "Financeiro:") é ignorado. */
export function pedacosDeEmail(texto: string | null | undefined): PedacoEmail[] {
  const out: PedacoEmail[] = [];
  for (const bruto of String(texto ?? '').replace(/[<>]/g, ' ').split(/[,;\s]+/)) {
    if (!bruto.includes('@')) continue;
    const email = bruto.trim().toLowerCase()
      .replace(/^mailto:/, '')
      .replace(/^[("'[{]+/, '')
      .replace(/[)"'\]}.,:;!?]+$/, '');
    out.push({ bruto: bruto.trim(), email, valido: EMAIL_RE.test(email) });
  }
  return out;
}

/** Variante A da reforma: o campo legado email_faturamento saiu da tela, mas
 *  o valor gravado não pode sumir — quando a lista de destinatários da NF
 *  está vazia, ele entra no editor como e-mail avulso SUGERIDO (chip âmbar;
 *  nada persiste até a pessoa mexer na lista e clicar em Salvar alterações). O campo era
 *  texto livre herdado do Notion e aceitava vários e-mails separados por
 *  vírgula/;. Lista já salva → devolve como está (sem sugestão). */
export function semearDestinatarios(
  salvos: Destinatario[] | null | undefined,
  emailLegado: string | null | undefined,
): { lista: Destinatario[]; sugerido: boolean; naoLidos: string[] } {
  const lista = Array.isArray(salvos) ? salvos : [];
  if (lista.length) return { lista, sugerido: false, naoLidos: [] };
  // 🚨 O leitor é o mesmo do campo de digitar (revisão adversarial de 21/09):
  // separando só por vírgula/;, "a@x b@y; c@z" (5 parcelas da Garden em aberto)
  // sugeria só c@z — e decidir a lista esvaziava o campo antigo, apagando a@x e
  // b@y sem eles nunca terem aparecido. `naoLidos` diz o que ficou de fora; com
  // algo de fora, quem chama NÃO esvazia o campo antigo.
  const vistos = new Set<string>();
  const avulsos: Destinatario[] = [];
  const naoLidos: string[] = [];
  for (const p of pedacosDeEmail(emailLegado)) {
    if (!p.valido) { naoLidos.push(p.bruto); continue; }
    if (vistos.has(p.email)) continue;
    vistos.add(p.email);
    avulsos.push({ tipo: 'email', email: p.email });
  }
  return avulsos.length
    ? { lista: avulsos, sugerido: true, naoLidos }
    : { lista, sugerido: false, naoLidos };
}

// ── Destinatários da NF: um salvar só (Lucas, 21/09/2026) ──────────────────
// Antes, o card de destinatários tinha botão PRÓPRIO ("Salvar destinatários")
// dentro de um modal que também tem Salvar — e os contatos escolhidos viravam
// só uma caixinha marcada no meio de uma lista que rola. Agora a lista vive no
// estado do ModalParcela e vai no MESMO "Salvar alterações" do rodapé; a tela
// mostra quem recebe numa fila de etiquetas, sempre à vista.

/** O mínimo de um contato do cliente que a régua precisa (types.ts tem o
 *  ContatoCliente completo; aqui fica estrutural para o jest não puxar nada). */
export type ContatoMin = {
  id: string; nome: string; email: string | null;
  papel?: string | null; cargo?: string | null;
};

/** Uma linha da fila "Quem recebe esta NF". `orfao` = contato que estava na
 *  lista salva mas não existe mais no cadastro do cliente (apagado, ou o
 *  "Faturar para" mudou). Ele aparece e pode ser tirado — antes contava no
 *  total sem aparecer em lugar nenhum, e o salvar seguinte o gravava de novo. */
export type DestinatarioVisivel = {
  chave: string; tipo: 'contato' | 'email';
  nome: string | null; email: string | null; papel: string | null; orfao: boolean;
};

const chaveDe = (d: Destinatario) =>
  d.tipo === 'contato' ? 'c:' + d.id : 'e:' + String(d.email || '').toLowerCase();

export function consolidarDestinatarios(
  lista: Destinatario[], contatos: ContatoMin[],
): DestinatarioVisivel[] {
  return lista.map(d => {
    if (d.tipo === 'contato') {
      const c = contatos.find(x => x.id === d.id);
      return {
        chave: chaveDe(d), tipo: 'contato' as const,
        nome: c ? c.nome : null, email: c ? (c.email || null) : null,
        papel: c ? (c.papel || null) : null, orfao: !c,
      };
    }
    return { chave: chaveDe(d), tipo: 'email' as const, nome: null,
      email: String(d.email || '').toLowerCase(), papel: null, orfao: false };
  });
}

/** Texto para colar no emissor da NF: "a@x.com; b@y.com", sem repetir e sem
 *  os órfãos (não há e-mail para eles). */
export function emailsParaCopiar(itens: DestinatarioVisivel[]): string {
  const vistos = new Set<string>();
  const out: string[] = [];
  for (const it of itens) {
    const e = (it.email || '').trim().toLowerCase();
    if (!e || vistos.has(e)) continue;
    vistos.add(e); out.push(e);
  }
  return out.join('; ');
}

export function removerDestinatario(lista: Destinatario[], chave: string): Destinatario[] {
  return lista.filter(d => chaveDe(d) !== chave);
}

/** Inclui um contato do cliente. Se o e-mail dele já estava como AVULSO, o
 *  avulso sai (a mesma pessoa não entra duas vezes). Contato sem e-mail não
 *  entra — não há para onde mandar. */
export function incluirContato(
  lista: Destinatario[], c: ContatoMin,
): { lista: Destinatario[]; aviso?: string } {
  if (!c.email) return { lista, aviso: `${c.nome} não tem e-mail no cadastro — complete em Comercial → Clientes.` };
  if (lista.some(d => d.tipo === 'contato' && d.id === c.id)) return { lista };
  const email = c.email.trim().toLowerCase();
  const semAvulsoIgual = lista.filter(d => !(d.tipo === 'email' && String(d.email || '').toLowerCase() === email));
  return { lista: [...semAvulsoIgual, { tipo: 'contato', id: c.id }] };
}

/** O que a pessoa digitou no campo de e-mail avulso. Aceita VÁRIOS de uma vez
 *  (colados de uma assinatura ou de outro e-mail: vírgula, ponto e vírgula,
 *  espaço ou quebra de linha), o formato "Nome <email>" e pontuação grudada
 *  ("(a@x.com)", "a@x.com.", "mailto:"). Minúsculas sempre. E-mail que é de um
 *  contato do cliente entra como o CONTATO — com nome. Devolve o que não entrou,
 *  com o porquê, e em `restante` o texto que deve FICAR no campo para a pessoa
 *  corrigir (o formato inválido — apagá-lo obrigava a redigitar tudo). */
export function incluirAvulsos(
  lista: Destinatario[], contatos: ContatoMin[], texto: string,
): { lista: Destinatario[]; incluidos: number; recusados: string[]; restante: string } {
  let nova = [...lista];
  let incluidos = 0;
  const recusados: string[] = [];
  const invalidos: string[] = [];
  const pedacos = pedacosDeEmail(texto);
  if (!pedacos.length && String(texto || '').trim()) {
    return { lista, incluidos: 0, recusados: [`"${String(texto).trim()}" não parece um e-mail`],
      restante: String(texto).trim() };
  }
  for (const { bruto, email, valido } of pedacos) {
    if (!valido) { recusados.push(`${bruto} (formato inválido)`); invalidos.push(bruto); continue; }
    const contato = contatos.find(c => (c.email || '').trim().toLowerCase() === email);
    const jaTem = nova.some(d =>
      (d.tipo === 'email' && String(d.email || '').toLowerCase() === email)
      || (d.tipo === 'contato' && contatos.find(c => c.id === d.id)?.email?.trim().toLowerCase() === email));
    if (jaTem) { recusados.push(`${email} (já está na lista)`); continue; }
    if (contato) {
      nova = incluirContato(nova, contato).lista;
    } else {
      nova = [...nova, { tipo: 'email', email }];
    }
    incluidos++;
  }
  return { lista: nova, incluidos, recusados, restante: invalidos.join(', ') };
}

/** O pedaço do payload do "Salvar alterações" que cuida dos destinatários.
 *  🚨 A listagem do CR NÃO traz emails_nf_destinatarios — só o detalhe. Por
 *  isso a chave só vai quando (1) o detalhe carregou e (2) a PESSOA mexeu na
 *  lista. Sem as duas travas, salvar o modal por outro motivo gravaria a lista
 *  vazia por cima da real, sem rastro na linha do tempo (é jsonb, não é
 *  rastreado) — o mesmo mecanismo do incidente de 21/08/2026.
 *  `legadoOferecido`: a lista veio sugerida do campo antigo email_faturamento.
 *  Decidir a lista (qualquer mexida) esvazia o campo antigo no mesmo POST —
 *  senão a sugestão ressuscita a cada abertura. */
export function montarPayloadDestinatarios(o: {
  lista: Destinatario[]; detalheOk: boolean; mexeu: boolean;
  legadoOferecido: boolean; abaNfEscondida: boolean;
}): { emails_nf_destinatarios?: Destinatario[]; email_faturamento?: string } {
  if (!o.detalheOk || !o.mexeu || o.abaNfEscondida) return {};
  const vistos = new Set<string>();
  const limpa: Destinatario[] = [];
  for (const d of o.lista) {
    const k = chaveDe(d);
    if (vistos.has(k)) continue;
    vistos.add(k);
    limpa.push(d.tipo === 'email'
      ? { tipo: 'email', email: String(d.email || '').trim().toLowerCase() }
      : { tipo: 'contato', id: d.id });
  }
  return {
    emails_nf_destinatarios: limpa,
    ...(o.legadoOferecido ? { email_faturamento: '' } : {}),
  };
}

// ── Um Salvar só (30/09/2026, Lucas: "o botão salvar deve servir para todas as
// alterações realizadas na parcela") ───────────────────────────────────────
// O rateio por setor tinha botão PRÓPRIO dentro da aba Detalhes: quem editava
// ali e salvava pelo rodapé não gravava o rateio, e quem clicava no rateio e
// fechava perdia o resto. Agora o card só EDITA; quem grava é o rodapé.

/** % por setor digitados na tela → o pedaço do payload, ou o erro em frase.
 *  Proporção canônica (4 casas), como o backend guarda. */
/** Os percentuais que o card do rateio MOSTRA ao abrir a edição (1 casa),
 *  a partir do que está gravado — que pode vir em proporção (0,6235) ou em
 *  R$ absoluto. É a SEMENTE: se a pessoa abriu o editor e não digitou nada,
 *  o Salvar não pode regravar, senão 0,6235 vira 0,624 sem ninguém pedir —
 *  e um rateio em três partes (33,3 + 33,3 + 33,3 = 99,9) seria RECUSADO
 *  sem a pessoa ter mexido nele. */
export function semearRateioPct(
  rateio: Record<string, number> | null | undefined, chaves: string[],
): Record<string, string> {
  const pares = Object.entries(rateio || {})
    .filter(([, v]) => typeof v === 'number' && v > 0);
  const total = pares.reduce((s, [, v]) => s + v, 0);
  const jaProporcao = total > 0.95 && total < 1.05;
  const out: Record<string, string> = {};
  for (const k of chaves) {
    const p = pares.find(([kk]) => kk === k);
    out[k] = p ? (((jaProporcao ? p[1] : p[1] / total) * 100).toFixed(1)) : '';
  }
  return out;
}

export function montarPayloadRateio(
  pct: Record<string, string> | null | undefined,
  semente?: Record<string, string> | null,
):
  { campos: { rateio_setores: Record<string, number> } } | { erro: string } | { campos: null } {
  if (!pct) return { campos: null };                       // ninguém mexeu
  // Abriu o editor e saiu igual ao que estava: não é alteração nenhuma.
  if (semente && Object.keys({ ...semente, ...pct })
    .every(k => String(pct[k] ?? '').trim() === String(semente[k] ?? '').trim())) {
    return { campos: null };
  }
  const novo: Record<string, number> = {};
  let soma = 0;
  for (const [k, v] of Object.entries(pct)) {
    const n = parseFloat(String(v ?? '').replace(',', '.')) || 0;
    if (n < 0) return { erro: 'O rateio por setor tem percentual negativo. Corrija na aba Detalhes.' };
    soma += n;
    if (n > 0) novo[k] = Math.round((n / 100) * 10000) / 10000;
  }
  // Tudo em branco = abriu o editor e não digitou nada (ou clicou no lápis
  // numa parcela que nunca teve rateio). Não é erro: é "não mexeu" — travar o
  // Salvar aqui deixaria a pessoa sem gravar o RESTO da parcela por causa de
  // um card que ela nem preencheu. Zerar um rateio existente continua sendo
  // impossível por aqui, como sempre foi.
  if (soma === 0) return { campos: null };
  // A folga leva um fio de epsilon: 33.3 + 33.3 + 33.4 dá 100.00000000000001
  // e 33.3 x 3 dá 99.89999999999999 em ponto flutuante.
  if (Math.abs(soma - 100) > 0.1 + 1e-9) {
    return { erro: `A soma do rateio por setor está em ${soma.toFixed(1)}% e precisa fechar 100%. `
      + 'Corrija na aba Detalhes (ou cancele a edição do rateio) e salve de novo.' };
  }
  return { campos: { rateio_setores: novo } };
}

/** Campos do formulário que diferem da base (o estado de quando o modal abriu
 *  e hidratou). Serve para o modal perguntar antes de fechar jogando fora o
 *  que foi digitado — vazio, nulo e "" contam como a mesma coisa, e número
 *  compara por valor ("100" === 100). */
export function camposAlterados(
  atual: Record<string, any>, base: Record<string, any> | null | undefined,
): string[] {
  if (!base) return [];
  const iguais = (a: any, b: any) => {
    const vazio = (x: any) => x === null || x === undefined || x === '';
    if (vazio(a) && vazio(b)) return true;
    // Um tem valor e o outro não: é alteração. Sem esta linha, trocar um
    // campo em branco por 0 (prazo_dias, descontos) passava por "igual",
    // porque Number(null) também é 0 — e o campo não seria salvo.
    if (vazio(a) !== vazio(b)) return false;
    if (typeof a === 'number' || typeof b === 'number') {
      const na = Number(a); const nb = Number(b);
      if (Number.isFinite(na) && Number.isFinite(nb)) return na === nb;
    }
    return String(a) === String(b);
  };
  return Object.keys({ ...base, ...atual }).filter(k => !iguais(atual[k], base[k]));
}
